import { useAuth } from '@clerk/clerk-react';
import {
  Button,
  Label,
  Progress,
  Alert,
  AlertDescription,
  AlertTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DataTable,
  type DataTableColumn,
} from '@startupquickstart/frontend';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from '@startupquickstart/frontend';
import {
  CheckCircle,
  AlertCircle,
  Download,
  Upload,
  ArrowRight,
  ArrowLeft,
  X,
  Loader2,
  ExternalLink,
  Check,
} from 'lucide-react';
import { CsvDropzone } from './CsvDropzone';
import { ValidationErrors, type ValidationError } from './ValidationErrors';
import { PaginatedValidationErrors } from './PaginatedValidationErrors';
import { ColumnMapper, type ColumnMappingInfo } from './ColumnMapper';
import {
  FuzzyMatchConfirm,
  type PendingFuzzyMatch,
  FUZZY_MATCH_CREATE_NEW,
  FUZZY_MATCH_LEAVE_BLANK,
} from './FuzzyMatchConfirm';
import { cn } from '@/lib/utils';

interface DuplicateInfo {
  rowNumber: number;
  inputName: string;
  existingEntityId: string;
  existingEntityName: string;
}

// Duplicate handling action types (matches backend)
type DuplicateActionType = 'skip' | 'import' | 'fill_types' | 'update_fill' | 'update_override';

const duplicateActionLabels: Record<DuplicateActionType, string> = {
  skip: 'Skip',
  import: 'Import as New',
  fill_types: 'Add Types Only',
  update_fill: 'Fill Missing Data',
  update_override: 'Override All Data',
};

type EntityType = 'attraction' | 'neighborhood' | 'tour';
type WizardStep = 'upload' | 'columns' | 'validation' | 'fuzzy' | 'importing' | 'results';

// Required columns for each entity type
const requiredColumns: Record<EntityType, string[]> = {
  attraction: ['name', 'streetAddress', 'city', 'state', 'country', 'greaterArea'],
  neighborhood: ['name', 'greaterArea'],
  tour: ['name', 'tourType'],
};

// Example CSV data for each entity type
const csvExamples: Record<EntityType, { headers: string[]; rows: string[][] }> = {
  attraction: {
    headers: ['name', 'streetAddress', 'streetAddress2', 'city', 'state', 'postalCode', 'country', 'greaterArea', 'neighborhood', 'description', 'longDescription', 'phoneNumber', 'email', 'website', 'attractionType1', 'attractionType2', 'attractionType3', 'attractionType4', 'attractionType5'],
    rows: [
      ['Jackson Square', '700 Decatur St', '', 'New Orleans', 'Louisiana', '70116', 'USA', 'New Orleans', 'French Quarter', 'Historic park', '', '504-555-1234', 'info@example.com', 'https://example.com', 'Historic Site', 'Park', 'Landmark', '', ''],
      ['Cafe Du Monde', '800 Decatur St', 'Suite 100', 'New Orleans', 'Louisiana', '70116', 'USA', 'New Orleans', 'French Quarter', 'Famous cafe', '', '', '', '', 'Restaurant', 'Cafe', 'Historic Site', '', ''],
    ],
  },
  neighborhood: {
    headers: ['name', 'greaterArea', 'description', 'city', 'state', 'country'],
    rows: [
      ['French Quarter', 'New Orleans', 'Historic heart of the city', 'New Orleans', 'Louisiana', 'USA'],
      ['Garden District', 'New Orleans', 'Elegant mansions and oak trees', 'New Orleans', 'Louisiana', 'USA'],
    ],
  },
  tour: {
    headers: ['name', 'tourType', 'description', 'tagline', 'greaterArea', 'duration', 'stops', 'difficulty', 'highlights'],
    rows: [
      ['French Quarter Walking Tour', 'Walking', 'Explore historic sites', 'Discover history', 'New Orleans', '2 hours', '8', 'Easy', 'Jackson Square, St. Louis Cathedral'],
      ['Garden District Tour', 'Walking', 'See beautiful homes', 'Southern charm', 'New Orleans', '1.5 hours', '6', 'Easy', 'Mansions, Gardens'],
    ],
  },
};

interface ImportSummary {
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
}

interface CsvImportWizardProps {
  onCancel: () => void;
  onImportComplete?: () => void;
  /** Session ID to resume an existing import that's awaiting confirmation */
  resumeSessionId?: string;
  /** Entity type for resumed session */
  resumeEntityType?: EntityType;
}

const entityTypeLabels: Record<EntityType, string> = {
  attraction: 'Attractions',
  neighborhood: 'Neighborhoods',
  tour: 'Tours',
};

const entityTypeRoutes: Record<EntityType, string> = {
  attraction: '/attractions',
  neighborhood: '/neighborhoods',
  tour: '/tours',
};

const stepTitles: Record<WizardStep, string> = {
  upload: 'Upload CSV',
  columns: 'Map Columns',
  validation: 'Validation Errors',
  fuzzy: 'Confirm Matches',
  importing: 'Importing',
  results: 'Results',
};

// Step Indicator Component
function StepIndicator({
  steps,
  currentStepIndex,
  onStepClick,
  disabledSteps = [],
}: {
  steps: WizardStep[];
  currentStepIndex: number;
  onStepClick?: (stepIndex: number) => void;
  disabledSteps?: WizardStep[];
}) {
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const isComplete = i < currentStepIndex;
        const isCurrent = i === currentStepIndex;
        const isClickable = isComplete && onStepClick && !disabledSteps.includes(s);

        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            {/* Step circle */}
            <button
              type="button"
              onClick={() => isClickable && onStepClick(i)}
              disabled={!isClickable}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all shrink-0',
                isComplete && 'bg-primary text-primary-foreground',
                isCurrent && 'bg-primary/10 text-primary border-2 border-primary',
                !isComplete && !isCurrent && 'bg-muted text-muted-foreground',
                isClickable && 'cursor-pointer hover:ring-2 hover:ring-primary/50 hover:ring-offset-2',
                !isClickable && 'cursor-default'
              )}
              title={isClickable ? `Go back to ${stepTitles[s]}` : undefined}
            >
              {isComplete ? <Check className="h-4 w-4" /> : i + 1}
            </button>

            {/* Connector line */}
            {i < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-2 transition-colors',
                  i < currentStepIndex ? 'bg-primary' : 'bg-muted'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CsvImportWizard({
  onCancel,
  onImportComplete,
  resumeSessionId,
  resumeEntityType,
}: CsvImportWizardProps) {
  const { getToken } = useAuth();

  // State
  const [step, setStep] = useState<WizardStep>(resumeSessionId ? 'fuzzy' : 'upload');
  const [entityType, setEntityType] = useState<EntityType>(resumeEntityType || 'attraction');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(resumeSessionId || null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [fuzzyMatches, setFuzzyMatches] = useState<PendingFuzzyMatch[]>([]);
  const [fuzzySelections, setFuzzySelections] = useState<Record<string, string | null>>({});
  const [duplicates, setDuplicates] = useState<DuplicateInfo[]>([]);
  const [duplicateSelections, setDuplicateSelections] = useState<Record<number, DuplicateActionType>>({});
  const [importProgress, setImportProgress] = useState(0);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingResume, setIsLoadingResume] = useState(!!resumeSessionId);

  // Column mapping state
  const [columnInfo, setColumnInfo] = useState<ColumnMappingInfo | null>(null);
  const [columnMappings, setColumnMappings] = useState<Record<string, string | null>>({});

  const steps: WizardStep[] = ['upload', 'columns', 'validation', 'fuzzy', 'importing', 'results'];
  const currentStepIndex = steps.indexOf(step);

  // Load existing session data when resuming
  // Supports multi-stage resume: column mapping, validation errors, or fuzzy matching
  useEffect(() => {
    if (!resumeSessionId) return;

    const loadSession = async () => {
      try {
        const token = await getToken();

        // 1. First fetch session details to determine state
        const sessionResponse = await fetch(
          `/api/v1/import/sessions/${resumeSessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!sessionResponse.ok) {
          throw new Error('Failed to load session');
        }

        const sessionData = await sessionResponse.json();
        const session = sessionData.data;

        // Update entity type from session
        if (session.entityType) {
          setEntityType(session.entityType as EntityType);
        }

        // 2. Handle terminal states - show results or error
        if (session.status === 'completed') {
          setImportSummary({
            totalRows: session.totalRows,
            successCount: session.successCount,
            errorCount: session.errorCount,
            skippedCount: session.skippedCount,
          });
          setStep('results');
          return;
        }

        if (session.status === 'cancelled' || session.status === 'failed') {
          setError(`Import session is ${session.status}`);
          setStep('results');
          return;
        }

        if (session.status === 'importing') {
          // Currently importing - show progress (user shouldn't normally see this)
          setStep('importing');
          setImportProgress(50);
          return;
        }

        // 3. Check if column mapping is needed
        // Session is pending and either has no column mappings or stored mappings are incomplete
        if (session.status === 'pending') {
          // Fetch column info to see if mapping step is needed
          const columnInfoResponse = await fetch(
            `/api/v1/import/sessions/${resumeSessionId}/column-info`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (columnInfoResponse.ok) {
            const columnInfoResult = await columnInfoResponse.json();
            const info = columnInfoResult.data;

            // Check if any required columns are unmapped
            const hasMissingRequired = info.requiredColumns.some(
              (col: string) => !info.currentMappings[col]
            );

            // Check if there are suggestions for unmapped columns
            const hasSuggestions = Object.keys(info.suggestions).length > 0;

            if (hasMissingRequired || hasSuggestions) {
              // Need to show column mapping step
              setColumnInfo(info);
              setColumnMappings(info.currentMappings || {});
              setStep('columns');
              return;
            }
          }

          // Column mappings are OK, proceed to validate
          // Fall through to validation logic below
        }

        // 4. For sessions that have validation errors stored, check if we need to show them
        if (session.errorCount > 0 &&
            (session.status === 'pending' || session.status === 'validating')) {
          // Has errors from previous validation - show validation step
          setStep('validation');
          return;
        }

        // 5. For awaiting_confirmation, validated, or pending sessions,
        //    re-validate to get current fuzzy matches and duplicates
        const validateResponse = await fetch(
          `/api/v1/import/sessions/${resumeSessionId}/validate`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!validateResponse.ok) {
          const errorData = await validateResponse.json();
          throw new Error(errorData.error || 'Failed to validate session');
        }

        const validationResult = await validateResponse.json();

        // If validation returned errors, show validation step
        if (validationResult.data.errors?.length > 0) {
          setValidationErrors(validationResult.data.errors);
          setStep('validation');
          return;
        }

        // Handle fuzzy matches
        if (validationResult.data.fuzzyMatches?.length > 0) {
          setFuzzyMatches(validationResult.data.fuzzyMatches);
          // Initialize selections - only auto-select if score > 90%
          const initialSelections: Record<string, string | null> = {};
          for (const match of validationResult.data.fuzzyMatches) {
            const key = `${match.field}:${match.inputValue}`;
            // Only auto-select if top suggestion has score > 90
            if (match.suggestions.length > 0 && match.suggestions[0].score > 90) {
              initialSelections[key] = match.suggestions[0].id;
            } else {
              initialSelections[key] = null; // Require manual selection
            }
          }
          setFuzzySelections(initialSelections);
        }

        // Handle duplicates
        if (validationResult.data.duplicates?.length > 0) {
          setDuplicates(validationResult.data.duplicates);
          const initialDuplicateSelections: Record<number, DuplicateActionType> = {};
          for (const dup of validationResult.data.duplicates) {
            initialDuplicateSelections[dup.rowNumber] = 'skip' as DuplicateActionType;
          }
          setDuplicateSelections(initialDuplicateSelections);
        }

        // Move to fuzzy step (even if empty, user can proceed to execute)
        setStep('fuzzy');

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
        toast.error('Failed to load import session');
      } finally {
        setIsLoadingResume(false);
      }
    };

    loadSession();
  }, [resumeSessionId, getToken]);

  const reset = useCallback(() => {
    setStep('upload');
    setSelectedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    setSessionId(null);
    setValidationErrors([]);
    setFuzzyMatches([]);
    setFuzzySelections({});
    setDuplicates([]);
    setDuplicateSelections({});
    setImportProgress(0);
    setImportSummary(null);
    setError(null);
    // Reset column mapping state
    setColumnInfo(null);
    setColumnMappings({});
  }, []);

  // Reload session data and show confirmation screen
  const reloadSessionForConfirmation = useCallback(async () => {
    if (!sessionId) return;

    setIsLoadingResume(true);
    setError(null);

    try {
      const token = await getToken();

      // Re-validate to get current fuzzy matches and duplicates
      const validateResponse = await fetch(
        `/api/v1/import/sessions/${sessionId}/validate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!validateResponse.ok) {
        throw new Error('Failed to load session');
      }

      const validationResult = await validateResponse.json();

      // Handle fuzzy matches
      if (validationResult.data.fuzzyMatches?.length > 0) {
        setFuzzyMatches(validationResult.data.fuzzyMatches);
        // Initialize selections - only pre-select if score > 90%
        const initialSelections: Record<string, string | null> = {};
        for (const match of validationResult.data.fuzzyMatches) {
          const key = `${match.field}:${match.inputValue}`;
          if (match.suggestions.length > 0 && match.suggestions[0].score > 90) {
            initialSelections[key] = match.suggestions[0].id;
          }
        }
        setFuzzySelections(initialSelections);
      } else {
        setFuzzyMatches([]);
        setFuzzySelections({});
      }

      // Handle duplicates
      if (validationResult.data.duplicates?.length > 0) {
        setDuplicates(validationResult.data.duplicates);
        const initialDuplicateSelections: Record<number, DuplicateActionType> = {};
        for (const dup of validationResult.data.duplicates) {
          initialDuplicateSelections[dup.rowNumber] = 'skip' as DuplicateActionType;
        }
        setDuplicateSelections(initialDuplicateSelections);
      } else {
        setDuplicates([]);
        setDuplicateSelections({});
      }

      setStep('fuzzy');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session');
      toast.error('Failed to reload session');
    } finally {
      setIsLoadingResume(false);
    }
  }, [sessionId, getToken]);

  const handleClose = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  // Define executeImport first (no dependencies on other callbacks)
  const executeImport = useCallback(
    async (sid: string, resolved: Record<string, string>) => {
      setImportProgress(0);

      try {
        const token = await getToken();

        // Simulate progress
        const progressInterval = setInterval(() => {
          setImportProgress((prev) => Math.min(prev + 5, 90));
        }, 500);

        const response = await fetch(`/api/v1/import/sessions/${sid}/execute`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ resolvedMatches: resolved }),
        });

        clearInterval(progressInterval);
        setImportProgress(100);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Import failed');
        }

        const result = await response.json();
        setImportSummary(result.data.summary);
        setStep('results');

        if (result.data.summary.successCount > 0) {
          toast.success(
            `Successfully imported ${result.data.summary.successCount} ${entityType}${result.data.summary.successCount === 1 ? '' : 's'}`
          );
          onImportComplete?.();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import failed');
        toast.error(err instanceof Error ? err.message : 'Import failed');
        setStep('results');
      }
    },
    [getToken, entityType, onImportComplete]
  );

  // Define runValidation second (depends on executeImport)
  const runValidation = useCallback(async (sid: string, token: string | null) => {
    if (!token) {
      token = await getToken();
    }

    // Validate
    const validateResponse = await fetch(`/api/v1/import/sessions/${sid}/validate`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!validateResponse.ok) {
      const errorData = await validateResponse.json();
      throw new Error(errorData.error || 'Validation failed');
    }

    const validationResult = await validateResponse.json();

    // Process validation result
    if (validationResult.data.errors?.length > 0) {
      setValidationErrors(validationResult.data.errors);
      setStep('validation');
    } else if (validationResult.data.fuzzyMatches?.length > 0 || validationResult.data.duplicates?.length > 0) {
      // Handle fuzzy matches
      if (validationResult.data.fuzzyMatches?.length > 0) {
        setFuzzyMatches(validationResult.data.fuzzyMatches);
        // Initialize selections - only auto-select if score > 90%
        const initialSelections: Record<string, string | null> = {};
        for (const match of validationResult.data.fuzzyMatches) {
          const key = `${match.field}:${match.inputValue}`;
          // Only auto-select if top suggestion has score > 90
          if (match.suggestions.length > 0 && match.suggestions[0].score > 90) {
            initialSelections[key] = match.suggestions[0].id;
          } else {
            initialSelections[key] = null; // Require manual selection
          }
        }
        setFuzzySelections(initialSelections);
      }

      // Handle duplicates
      if (validationResult.data.duplicates?.length > 0) {
        setDuplicates(validationResult.data.duplicates);
        // Initialize all duplicates to skip by default
        const initialDuplicateSelections: Record<number, DuplicateActionType> = {};
        for (const dup of validationResult.data.duplicates) {
          initialDuplicateSelections[dup.rowNumber] = 'skip' as DuplicateActionType;
        }
        setDuplicateSelections(initialDuplicateSelections);
      }

      setStep('fuzzy');
    } else {
      // All valid, proceed to import
      setStep('importing');
      executeImport(sid, {});
    }
  }, [getToken, executeImport]);

  // Define uploadAndValidate third (depends on runValidation)
  const uploadAndValidate = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('entityType', entityType);

      // Upload progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      // Upload
      const uploadResponse = await fetch('/api/v1/import/upload', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      clearInterval(progressInterval);

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const uploadResult = await uploadResponse.json();
      const newSessionId = uploadResult.data.sessionId;
      setSessionId(newSessionId);

      setUploadProgress(95);

      // Check if there are missing or unknown columns that need mapping
      const hasMissingColumns = uploadResult.data.missingColumns?.length > 0;
      const hasUnknownColumns = uploadResult.data.unknownColumns?.length > 0;

      // Show column mapping step if any columns need attention
      if (hasMissingColumns || hasUnknownColumns) {
        // Fetch column mapping info
        const columnInfoResponse = await fetch(
          `/api/v1/import/sessions/${newSessionId}/column-info`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (columnInfoResponse.ok) {
          const columnInfoResult = await columnInfoResponse.json();
          setColumnInfo(columnInfoResult.data);
          setColumnMappings(columnInfoResult.data.currentMappings || {});
          setUploadProgress(100);
          setStep('columns');
          return;
        }
      }

      setUploadProgress(100);
      // No column issues, proceed to validation
      await runValidation(newSessionId, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, entityType, getToken, runValidation]);

  // Define confirmColumnMappings fourth (depends on runValidation)
  const confirmColumnMappings = useCallback(async () => {
    if (!sessionId) return;

    setIsUploading(true);
    setError(null);

    try {
      const token = await getToken();

      // Confirm column mappings
      const confirmResponse = await fetch(
        `/api/v1/import/sessions/${sessionId}/confirm-columns`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ columnMappings }),
        },
      );

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        throw new Error(errorData.error || 'Failed to confirm column mappings');
      }

      // Now run validation
      await runValidation(sessionId, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      toast.error(err instanceof Error ? err.message : 'Failed to confirm columns');
    } finally {
      setIsUploading(false);
    }
  }, [sessionId, columnMappings, getToken, runValidation]);

  const confirmFuzzyMatches = useCallback(async () => {
    if (!sessionId) return;

    setIsUploading(true);
    setError(null);

    try {
      const token = await getToken();

      // Build confirmed matches payload with support for create new and leave blank
      const matches = Object.entries(fuzzySelections).map(([key, value]) => {
        const [field, ...rest] = key.split(':');
        const inputValue = rest.join(':'); // Handle values that contain ':'
        return {
          field,
          inputValue,
          selectedId: value === FUZZY_MATCH_LEAVE_BLANK ? null : value,
          createNew: value === FUZZY_MATCH_CREATE_NEW,
        };
      });

      // Build duplicate actions (new enhanced format)
      const duplicateActions = Object.entries(duplicateSelections).map(([rowNumberStr, action]) => ({
        rowNumber: parseInt(rowNumberStr, 10),
        action,
      }));

      const response = await fetch(`/api/v1/import/sessions/${sessionId}/confirm-matches`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ matches, duplicateActions }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Confirmation failed');
      }

      const result = await response.json();

      // Check if there are NEW fuzzy matches or duplicates that need confirmation
      // This can happen when resolving one field (e.g., greaterArea) enables checking another (e.g., neighborhood)
      const hasNewFuzzyMatches = result.data.fuzzyMatches?.length > 0;
      const hasNewDuplicates = result.data.duplicates?.length > 0;

      console.log('confirmMatches response:', {
        fuzzyMatches: result.data.fuzzyMatches,
        duplicates: result.data.duplicates,
        hasNewFuzzyMatches,
        hasNewDuplicates,
      });

      if (hasNewFuzzyMatches || hasNewDuplicates) {
        // Update state with new fuzzy matches/duplicates and stay on confirmation step
        console.log('Setting new fuzzy matches:', result.data.fuzzyMatches);
        if (hasNewFuzzyMatches) {
          setFuzzyMatches(result.data.fuzzyMatches);
          // Initialize selections - only auto-select if score > 90%
          const newSelections: Record<string, string | null> = {};
          for (const match of result.data.fuzzyMatches) {
            const key = `${match.field}:${match.inputValue}`;
            // Only auto-select if top suggestion has score > 90
            if (match.suggestions?.length > 0 && match.suggestions[0].score > 90) {
              newSelections[key] = match.suggestions[0].id;
            } else {
              newSelections[key] = null; // Require manual selection
            }
          }
          setFuzzySelections(newSelections);
        } else {
          setFuzzyMatches([]);
          setFuzzySelections({});
        }

        if (hasNewDuplicates) {
          setDuplicates(result.data.duplicates);
          const newDupSelections: Record<number, DuplicateActionType> = {};
          for (const dup of result.data.duplicates) {
            newDupSelections[dup.rowNumber] = 'skip';
          }
          setDuplicateSelections(newDupSelections);
        } else {
          setDuplicates([]);
          setDuplicateSelections({});
        }

        toast.info('Additional items need confirmation');
        return; // Stay on fuzzy step
      }

      // Build resolved matches for import execution
      const resolved: Record<string, string> = {};

      // Add user-selected matches (excluding leave blank and create new placeholders)
      for (const [key, value] of Object.entries(fuzzySelections)) {
        if (value && value !== FUZZY_MATCH_LEAVE_BLANK && value !== FUZZY_MATCH_CREATE_NEW) {
          const [, ...rest] = key.split(':');
          const inputValue = rest.join(':');
          resolved[inputValue] = value;
        }
      }

      // Add newly created entities from the backend response
      if (result.data.createdEntities) {
        for (const entity of result.data.createdEntities) {
          resolved[entity.inputValue] = entity.id;
        }
      }

      // Proceed to import - errors will cause those specific rows to be skipped
      if (result.data.errors?.length > 0) {
        setValidationErrors(result.data.errors);
      }
      setStep('importing');
      executeImport(sessionId, resolved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      toast.error(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setIsUploading(false);
    }
  }, [sessionId, fuzzySelections, duplicateSelections, getToken, executeImport]);

  const downloadErrors = useCallback(async () => {
    if (!sessionId) return;

    try {
      const token = await getToken();
      const response = await fetch(`/api/v1/import/sessions/${sessionId}/errors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download errors');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-errors-${sessionId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error('Failed to download error report');
    }
  }, [sessionId, getToken]);

  const canProceed = (): boolean => {
    switch (step) {
      case 'upload':
        return !!selectedFile && !isUploading;
      case 'columns': {
        // All required columns must be mapped
        if (!columnInfo) return false;
        const requiredMapped = columnInfo.requiredColumns.every(
          (col) => columnMappings[col],
        );
        return requiredMapped && !isUploading;
      }
      case 'fuzzy': {
        // All fuzzy matches must have a selection (not null or undefined)
        // Special values like __CREATE_NEW__ and __LEAVE_BLANK__ are valid selections
        const fuzzyComplete =
          fuzzyMatches.length === 0 ||
          fuzzyMatches.every((m) => {
            const key = `${m.field}:${m.inputValue}`;
            const selection = fuzzySelections[key];
            // Must have an actual selection (not null or undefined)
            // Special markers like __CREATE_NEW__ and __LEAVE_BLANK__ count as selections
            return selection !== undefined && selection !== null;
          });

        // All duplicates must have a selection
        const duplicatesComplete =
          duplicates.length === 0 ||
          duplicates.every((d) => duplicateSelections[d.rowNumber] !== undefined);

        // Can proceed if all items are resolved
        // For resumed validated sessions with no pending items, this allows immediate execution
        return fuzzyComplete && duplicatesComplete && !isLoadingResume;
      }
      default:
        return false;
    }
  };

  const handleNext = () => {
    switch (step) {
      case 'upload':
        uploadAndValidate();
        break;
      case 'columns':
        confirmColumnMappings();
        break;
      case 'fuzzy':
        confirmFuzzyMatches();
        break;
    }
  };

  // Back navigation - determine the previous navigable step
  const getPreviousStep = (currentStep: WizardStep): WizardStep | null => {
    switch (currentStep) {
      case 'columns':
        return 'upload';
      case 'validation':
        // Go back to columns if we have column info, otherwise upload
        return columnInfo ? 'columns' : 'upload';
      case 'fuzzy':
        // Go back to columns if we have column info, otherwise upload
        // Skip validation step since it's an error state
        return columnInfo ? 'columns' : 'upload';
      default:
        return null;
    }
  };

  const canGoBack = (): boolean => {
    // Can't go back from upload, importing, or results
    if (step === 'upload' || step === 'importing' || step === 'results') {
      return false;
    }
    // Can't go back while loading or uploading
    if (isUploading || isLoadingResume) {
      return false;
    }
    return true;
  };

  const handleBack = () => {
    const prevStep = getPreviousStep(step);
    if (prevStep) {
      setStep(prevStep);
      // Clear any errors when going back
      setError(null);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    const targetStep = steps[stepIndex];
    // Only allow navigating to steps we can actually go back to
    if (targetStep && stepIndex < currentStepIndex) {
      // Don't allow clicking on validation step directly (it's an error state)
      if (targetStep === 'validation') {
        return;
      }
      setStep(targetStep);
      setError(null);
    }
  };

  // Column definitions for duplicates DataTable
  const duplicateColumns: DataTableColumn<DuplicateInfo>[] = useMemo(
    () => [
      {
        key: 'rowNumber',
        label: 'Row',
        format: (value: number) => (
          <span className="font-mono text-muted-foreground text-sm">{value}</span>
        ),
      },
      {
        key: 'inputName',
        label: 'CSV Name',
        format: (value: string) => <span className="font-medium">{value}</span>,
      },
      {
        key: 'existingEntityName',
        label: 'Existing Record',
        format: (value: string, row: DuplicateInfo) => (
          <a
            href={`${entityTypeRoutes[entityType]}/${row.existingEntityId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline font-medium"
          >
            {value}
            <ExternalLink className="h-3 w-3" />
          </a>
        ),
      },
      {
        key: 'rowNumber' as keyof DuplicateInfo,
        label: 'Action',
        align: 'center',
        format: (_: number, row: DuplicateInfo) => {
          // Show all options for attractions, limited options for other entity types
          const availableActions: DuplicateActionType[] = entityType === 'attraction'
            ? ['skip', 'import', 'fill_types', 'update_fill', 'update_override']
            : ['skip', 'import'];

          return (
            <Select
              value={duplicateSelections[row.rowNumber] || 'skip'}
              onValueChange={(value: DuplicateActionType) => {
                setDuplicateSelections((prev) => ({
                  ...prev,
                  [row.rowNumber]: value,
                }));
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableActions.map((action) => (
                  <SelectItem key={action} value={action}>
                    {duplicateActionLabels[action]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      },
    ],
    [entityType, duplicateSelections]
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full">
            <X className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Import {entityTypeLabels[entityType]}</h1>
            <p className="text-muted-foreground mt-1">
              {stepTitles[step]}
            </p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="space-y-2">
        <StepIndicator
          steps={steps}
          currentStepIndex={currentStepIndex}
          onStepClick={handleStepClick}
          disabledSteps={['validation', 'importing', 'results']}
        />
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground text-center">
          Step {currentStepIndex + 1} of {steps.length}
        </p>
      </div>

      {/* Show general error alert only if not on results step (results has its own error display) */}
      {error && step !== 'results' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-muted/30 px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight">Select Data Type & Upload File</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Choose the type of data you want to import and upload your CSV file.
            </p>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-sm font-medium">What would you like to import?</Label>
              <Select
                value={entityType}
                onValueChange={(v) => setEntityType(v as EntityType)}
                disabled={isUploading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attraction">Attractions</SelectItem>
                  <SelectItem value="neighborhood">Neighborhoods</SelectItem>
                  <SelectItem value="tour">Tours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <CsvDropzone
              onFileSelect={setSelectedFile}
              selectedFile={selectedFile}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              disabled={isUploading}
            />

            {/* CSV Format Example */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Your CSV should have these columns (<span className="font-semibold text-primary">blue</span> = required):
              </p>
              <div className="rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow>
                        {csvExamples[entityType].headers.map((header) => (
                          <TableHead key={header} className={cn('whitespace-nowrap', requiredColumns[entityType].includes(header) && 'font-semibold text-primary')}>
                            {header}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvExamples[entityType].rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <TableCell key={cellIndex} className="whitespace-nowrap">
                              {cell || <span className="text-muted-foreground/50">—</span>}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleNext} disabled={!canProceed()} className="gap-2">
              <Upload className="h-4 w-4" />
              Upload & Validate
            </Button>
          </div>
        </div>
      )}

      {/* Step: Column Mapping */}
      {step === 'columns' && columnInfo && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-amber-500/10 px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight">Map CSV Columns</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Some columns in your CSV don't match the expected format. Please map them to the correct system fields.
            </p>
          </div>
          <div className="p-6">
            <ColumnMapper
              info={columnInfo}
              mappings={columnMappings}
              onMappingChange={(systemCol, csvCol) => {
                setColumnMappings((prev) => ({
                  ...prev,
                  [systemCol]: csvCol,
                }));
              }}
            />
          </div>
          <div className="border-t bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            <Button variant="outline" onClick={handleBack} disabled={!canGoBack()} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={!canProceed()} className="gap-2">
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Validating...
                </>
              ) : (
                <>
                  Confirm & Validate
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step: Validation Errors */}
      {step === 'validation' && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-destructive/10 px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight text-destructive">Validation Failed</h2>
            <p className="text-sm text-muted-foreground mt-1">
              The following errors were found in your CSV file. Please fix them and try again.
            </p>
          </div>
          <div className="p-6 space-y-4">
            {sessionId ? (
              <PaginatedValidationErrors
                sessionId={sessionId}
                initialErrors={validationErrors}
              />
            ) : (
              <ValidationErrors errors={validationErrors} />
            )}
          </div>
          <div className="border-t bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            <Button variant="outline" onClick={handleBack} disabled={!canGoBack()} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <Button variant="outline" onClick={downloadErrors} className="gap-2">
                <Download className="h-4 w-4" />
                Download Error Report
              </Button>
              <Button variant="outline" onClick={reset}>
                Try Again
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step: Fuzzy Match Confirmation */}
      {step === 'fuzzy' && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-muted/30 px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight">Review & Confirm</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Please review the items below before proceeding with the import.
            </p>
          </div>
          {isLoadingResume ? (
            <div className="py-16 px-6 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-4 text-muted-foreground">Loading import session...</p>
            </div>
          ) : (
          <div className="p-6 space-y-8">
            {/* Ready to Import Message (no fuzzy matches or duplicates) */}
            {fuzzyMatches.length === 0 && duplicates.length === 0 && (
              <div className="py-8 text-center">
                <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold tracking-tight">Ready to Import</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Your CSV has been validated. Click "Confirm & Import" to proceed with the import.
                </p>
              </div>
            )}

            {/* Fuzzy Matches Section */}
            {fuzzyMatches.length > 0 && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold tracking-tight">Confirm Matches</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some values in your CSV closely match existing records. Select the correct match for each, create a new record, or leave blank.
                  </p>
                </div>
                <FuzzyMatchConfirm
                  matches={fuzzyMatches}
                  selections={fuzzySelections}
                  onSelectionChange={(key, value) => {
                    setFuzzySelections((prev) => ({ ...prev, [key]: value }));
                  }}
                />
              </div>
            )}

            {/* Duplicates Section */}
            {duplicates.length > 0 && (
              <div className="space-y-4">
                {fuzzyMatches.length > 0 && <hr className="border-t" />}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold tracking-tight">
                      Potential Duplicates ({duplicates.length})
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      These rows appear to match existing records. Choose whether to skip or import them anyway.
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Select
                      onValueChange={(value: DuplicateActionType) => {
                        const allSame: Record<number, DuplicateActionType> = {};
                        duplicates.forEach((d) => {
                          allSame[d.rowNumber] = value;
                        });
                        setDuplicateSelections(allSame);
                      }}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Set all to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">Skip All</SelectItem>
                        <SelectItem value="import">Import All</SelectItem>
                        {entityType === 'attraction' && (
                          <>
                            <SelectItem value="fill_types">Add Types to All</SelectItem>
                            <SelectItem value="update_fill">Fill Missing in All</SelectItem>
                            <SelectItem value="update_override">Override All</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DataTable<DuplicateInfo>
                  columns={duplicateColumns}
                  data={duplicates}
                />

                {/* Help section explaining actions */}
                {entityType === 'attraction' && (
                  <div className="rounded-lg bg-muted/50 border p-4 mt-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                      Action Descriptions
                    </h4>
                    <dl className="text-sm space-y-2">
                      <div>
                        <dt className="font-medium text-foreground">Skip</dt>
                        <dd className="text-muted-foreground ml-4">Don't import this row. The existing record remains unchanged.</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">Import as New</dt>
                        <dd className="text-muted-foreground ml-4">Create a new record even though a similar one exists (creates a duplicate).</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">Add Types Only</dt>
                        <dd className="text-muted-foreground ml-4">Only add new attraction types from the CSV to the existing record. Other fields are not modified.</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">Fill Missing Data</dt>
                        <dd className="text-muted-foreground ml-4">Update only empty/null fields in the existing record with values from the CSV. Also adds any new attraction types.</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-foreground">Override All Data</dt>
                        <dd className="text-muted-foreground ml-4">Replace CSV-importable fields (address, description, contact info, coordinates) with values from the CSV. Also adds any new attraction types. Fields not in the CSV (name, status, images, tours, enrichment data) are preserved.</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
          {!isLoadingResume && (
          <div className="border-t bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            <Button variant="outline" onClick={handleBack} disabled={!canGoBack() || isUploading} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={!canProceed() || isUploading} className="gap-2">
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm & Import
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="py-16 px-6">
            <div className="text-center space-y-6">
              {/* Animated icon */}
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                <div className="relative w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="h-8 w-8 text-primary animate-pulse" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-semibold tracking-tight">Importing your data...</h3>
                <p className="text-sm text-muted-foreground">
                  This may take a moment. Please don't close this page.
                </p>
              </div>

              {/* Progress bar */}
              <div className="max-w-xs mx-auto space-y-2">
                <Progress value={importProgress} className="h-2" />
                <p className="text-sm font-medium text-muted-foreground">{importProgress}% complete</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step: Results - Error State (no summary) */}
      {step === 'results' && !importSummary && error && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-destructive/10 px-6 py-4">
            <h2 className="text-lg font-medium tracking-tight text-destructive">Import Failed</h2>
            <p className="text-sm text-muted-foreground mt-1">
              The import could not be completed due to an error.
            </p>
          </div>
          <div className="p-8">
            <div className="text-center space-y-6">
              {/* Error icon */}
              <div className="relative mx-auto w-24 h-24">
                <div className="absolute inset-0 rounded-full bg-destructive/20" />
                <div className="absolute inset-3 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="h-12 w-12 text-destructive" />
                </div>
              </div>

              {/* Error message */}
              <div className="space-y-3 max-w-md mx-auto">
                <h3 className="text-xl font-semibold tracking-tight">Something went wrong</h3>
                <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4">
                  <p className="text-sm text-destructive font-medium">{error}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {error.includes('awaiting_confirmation') || error.includes('validated')
                    ? 'The import session needs confirmation before it can be executed. You can go back and review the pending items.'
                    : 'Please try again or contact support if the problem persists.'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-center gap-3 pt-4">
                {(error.includes('awaiting_confirmation') || error.includes('needs_confirmation')) && (
                  <Button
                    variant="default"
                    onClick={reloadSessionForConfirmation}
                    className="gap-2"
                  >
                    <ArrowRight className="h-4 w-4 rotate-180" />
                    Go Back to Confirmation
                  </Button>
                )}
                <Button variant="outline" onClick={reset}>
                  Start Over
                </Button>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step: Results - Success State (has summary) */}
      {step === 'results' && importSummary && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="py-16 px-6">
            <div className="text-center space-y-8">
              {/* Success/Error icon */}
              {importSummary.successCount > 0 ? (
                <div className="relative mx-auto w-20 h-20">
                  <div className="absolute inset-0 rounded-full bg-emerald-100 dark:bg-emerald-900/30" />
                  <div className="absolute inset-2 rounded-full bg-emerald-50 dark:bg-emerald-900/50 flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
              ) : (
                <div className="relative mx-auto w-20 h-20">
                  <div className="absolute inset-0 rounded-full bg-destructive/20" />
                  <div className="absolute inset-2 rounded-full bg-destructive/10 flex items-center justify-center">
                    <AlertCircle className="h-10 w-10 text-destructive" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-2xl font-semibold tracking-tight">Import Complete</h3>
                <p className="text-muted-foreground">
                  {importSummary.successCount > 0
                    ? `Successfully imported ${importSummary.successCount} records`
                    : 'No records were imported'}
                </p>
              </div>

              {/* Summary stats */}
              <div className="flex justify-center gap-8">
                <div className="text-center">
                  <div className="text-3xl font-semibold text-emerald-600 dark:text-emerald-400">
                    {importSummary.successCount}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-1">
                    Imported
                  </div>
                </div>
                {importSummary.errorCount > 0 && (
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-destructive">
                      {importSummary.errorCount}
                    </div>
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-1">
                      Errors
                    </div>
                  </div>
                )}
                {importSummary.skippedCount > 0 && (
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-amber-600 dark:text-amber-400">
                      {importSummary.skippedCount}
                    </div>
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-1">
                      Skipped
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-center gap-3 pt-4">
                {importSummary.errorCount > 0 && (
                  <Button variant="outline" onClick={downloadErrors} className="gap-2">
                    <Download className="h-4 w-4" />
                    Download Error Report
                  </Button>
                )}
                <Button variant="outline" onClick={reset}>
                  Import More
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
